import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import Navbar from '../components/Navbar';
import Select from 'react-select';
import './BookingForm.css';

const BookingForm = () => {
  const [form, setForm] = useState({});
  const [customers, setCustomers] = useState([]);
  const { id } = useParams(); // determines edit mode
  const navigate = useNavigate();
  const editMode = !!id;

  // Fetch customers
  useEffect(() => {
    api.get('/customers').then(res => {
      const options = res.data.map(c => ({
        value: c.id,
        label: c.customername,
        address: c.address,
        city: c.city,
        state: c.state,
        pincode: c.pincode,
        country: c.country,
      }));
      setCustomers(options);
    });
  }, []);

  // Fetch booking data if in edit mode
  useEffect(() => {
    if (editMode) {
      api.get(`/bookings/${id}`)
        .then(res => setForm(res.data))
        .catch(err => console.error('Failed to fetch booking:', err));
    }
  }, [editMode, id]);

  const handleCustomerSelect = selected => {
    setForm(prev => ({
      ...prev,
      customerid: selected.value,
      customername: selected.label,
      pickup_address: selected.address,
      pickup_city: selected.city,
      pickup_state: selected.state,
      pickup_pincode: selected.pincode,
      pickup_country: selected.country,
    }));
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      if (editMode) {
        await api.put(`/bookings/${id}`, form);
      } else {
        await api.post('/bookings', form);
      }
      navigate('/bookings');
    } catch (err) {
      console.error('Submit error:', err);
      alert('Failed to save booking');
    }
  };

  return (
    <>
      <Navbar />
      <div className="booking-form-container">
        <h2>{editMode ? 'Edit Booking' : 'Add Booking'}</h2>

        <form className="booking-form" onSubmit={handleSubmit}>

          {/* Row: Customer */}
          <div className="form-row">
            <div className="form-group">
              <label>Customer Name</label>
              <Select
                options={customers}
                onChange={handleCustomerSelect}
                placeholder="Select customer..."
                isSearchable
                value={customers.find(c => c.value === form.customerid) || null}
              />
            </div>
          </div>

          {/* Pickup Address */}
          <div className="form-row">
            <div className="form-group wide">
              <label>Pickup Address</label>
              <input name="pickup_address" value={form.pickup_address || ''} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Pickup City</label>
              <input name="pickup_city" value={form.pickup_city || ''} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Pickup State</label>
              <input name="pickup_state" value={form.pickup_state || ''} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Pickup Pincode</label>
              <input name="pickup_pincode" value={form.pickup_pincode || ''} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Pickup Country</label>
              <input name="pickup_country" value={form.pickup_country || ''} onChange={handleChange} required />
            </div>
          </div>

          {/* Delivery Address */}
          <div className="form-row">
            <div className="form-group wide">
              <label>Delivery Address</label>
              <input name="delivery_address" value={form.delivery_address || ''} onChange={handleChange} required />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Delivery City</label>
              <input name="delivery_city" value={form.delivery_city || ''} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Delivery State</label>
              <input name="delivery_state" value={form.delivery_state || ''} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Delivery Pincode</label>
              <input name="delivery_pincode" value={form.delivery_pincode || ''} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Delivery Country</label>
              <input name="delivery_country" value={form.delivery_country || ''} onChange={handleChange} required />
            </div>
          </div>

          {/* Booking Info */}
          <div className="form-row">
            <div className="form-group">
              <label>AWB Number</label>
              <input name="awbnumber" value={form.awbnumber || ''} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Reference Number</label>
              <input name="reference_number" value={form.reference_number || ''} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Weight (kg)</label>
              <input type="number" name="weight" value={form.weight || ''} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Status</label>
              <input name="status" value={form.status || ''} onChange={handleChange} required />
            </div>
          </div>

          {/* Only show on Edit */}
          {editMode && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Delivery Date</label>
                  <input type="date" name="deliverydate" value={form.deliverydate ? form.deliverydate.split('T')[0] : ''} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Delivery By</label>
                  <input name="deliveryby" value={form.deliveryby || ''} onChange={handleChange} />
                </div>
                <div className="form-group wide">
                  <label>Delivery Note</label>
                  <input name="deliverynote" value={form.deliverynote || ''} onChange={handleChange} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Created By</label>
                  <input name="createdby" value={form.createdby || ''} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Last Updated By</label>
                  <input name="lastUpdatedBy" value={form.lastUpdatedBy || ''} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Updated By</label>
                  <input name="updatedBy" value={form.updatedBy || ''} onChange={handleChange} />
                </div>
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="submit">{editMode ? 'Update Booking' : 'Add Booking'}</button>
          </div>
        </form>
      </div>
    </>
  );
};

export default BookingForm;
